import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  disabled?: boolean;
  /** Focus the selected option on mount (pages focus their first input). */
  autoFocus?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
  itemClassName?: string;
}

/**
 * Pill radio group: one value from a short row of options. Built on Radix
 * RadioGroup, so it has real radio semantics for keyboard users (one tab
 * stop, arrow keys move and select).
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  disabled,
  autoFocus,
  className,
  itemClassName,
  ...ariaProps
}: SegmentedControlProps<T>) {
  return (
    <RadioGroupPrimitive.Root
      className={cn(
        'bg-muted inline-flex h-8 gap-0.5 rounded-md p-0.5',
        className,
      )}
      disabled={disabled}
      onValueChange={(next) => onValueChange(next as T)}
      orientation="horizontal"
      value={value}
      {...ariaProps}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          autoFocus={autoFocus && option.value === value}
          className={cn(
            'focus-visible:ring-ring/50 text-muted-foreground hover:text-foreground data-[state=checked]:bg-background data-[state=checked]:text-foreground h-full cursor-pointer rounded-[4px] px-3 text-[13px] font-medium transition-colors duration-100 outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:shadow-[0_1px_2px_rgba(0,0,0,0.08)]',
            itemClassName,
          )}
          disabled={option.disabled}
          key={option.value}
          value={option.value}
        >
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
